import React from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import HasRolePermission from "components/shared/has-role-permission";
import { formatDate } from "utils/dates";
import { Link } from "react-router-dom";
import { InlineWaiting, Checkbox, IconButton } from "components/ui";
import { Formik, Form } from "formik";
import {
  MdEdit,
  MdSave,
  MdCancel,
  MdWarning,
  MdFindReplace,
  MdDelete,
  MdInfoOutline
} from "react-icons/lib/md";
import FormikField from "components/forms/formik-field";
import { PatientProfileContext } from "components/profile/context";
import { useFetch } from "hooks";
import * as msg from "actions/message";
import { put } from "utils/api";
import { getApiErrorMessage } from "utils/misc";

const HcpcRow = ({
  eligibility,
  expanded,
  toggleExpanded,
  editRow,
  setEditRow,
  patientId
}) => {
  const dispatch = useDispatch();
  const { fetchPatientHCPCHistoryAPI } = React.useContext(
    PatientProfileContext
  );
  const updatePatientHCPCHistoryAPI = useFetch({
    apiFn: values => put(`patients/${patientId}/edit-hcpcs-ordered`, values),
    onSuccess: () => {
      fetchPatientHCPCHistoryAPI.fetchData(patientId);
    },
    onError: err => {
      dispatch(
        msg.errorMessage(
          getApiErrorMessage(err, "Error updating patient HCPCS History record")
        )
      );
    }
  });
  return (
    <>
      <div className="eligibility-table-row" onClick={toggleExpanded}>
        <div style={{ color: eligibility.isEligible ? "green" : "default" }}>
          {eligibility.productType}
        </div>
        <div>
          {eligibility.insuranceRuleMissing ? (
            // S3DEV2-7525: item has no matching insurance rule, so no eligibility date
            // could be calculated. Show an explicit label instead of a blank cell.
            <span
              title="No insurance rule is configured for this item, so an eligibility date can't be calculated."
              style={{ display: "flex", alignItems: "center", gap: 4, color: "#a15c00" }}
            >
              No insurance rule
              <MdInfoOutline size={16} color="#a15c00" />
            </span>
          ) : (
            formatDate(eligibility.eligibilityDate)
          )}
        </div>
        <div
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <span>{formatDate(eligibility.lastOrderDate)}</span>
          {eligibility.history.some(x => x.replacementItem) && (
            <span
              style={{
                paddingLeft: 5,
                display: "flex",
                alignItems: "baseline"
              }}
            >
              <span title="Replacement Item on file">
                <MdFindReplace size={20} color="#ff7800" />
              </span>
            </span>
          )}
          {eligibility.lastOrderedItemZeroBilled && (
            <span
              style={{
                paddingLeft: 5,
                display: "flex",
                alignItems: "baseline"
              }}
            >
              <span title="Last Item Ordered was Zero Billed">
                <MdWarning size={18} color="#ff7800" />
              </span>
            </span>
          )}
          {eligibility.lastOrderedItemPrivatePay && (
            <span
              style={{
                paddingLeft: 5,
                display: "flex",
                alignItems: "baseline"
              }}
            >
              <span title="Last Item Ordered was Private Pay">
                <MdWarning size={18} color="#ff7800" />
              </span>
            </span>
          )}
          {eligibility.adjustedItemQty > 0 && (
            <span
              style={{
                paddingLeft: 5,
                display: "flex",
                alignItems: "baseline"
              }}
            >
              <span
                title={`Adjusted eligible quantity of last item to ${eligibility.adjustedItemQty}`}
              >
                <MdWarning size={18} color="#dd9830" />
              </span>
            </span>
          )}
        </div>
        <div className="hcpc-truncate" style={{ fontSize: "0.9rem" }} title={eligibility.lastOrderedItemName}>
          {eligibility.lastOrderedItemName}
        </div>
        <div className="hcpc-truncate" style={{ fontSize: "0.9rem" }} title={eligibility.lastOrderedItemId}>
          {eligibility.lastOrderedItemId}
        </div>
        {eligibility.insRuleDaysToPush == null ? (
          <div />
        ) : (
          <div>
            {eligibility.insRuleDaysToPush}|{eligibility.insRuleQtyAllowed}|
            {eligibility.companyOffsetDays}
          </div>
        )}
      </div>
      {expanded && (
        <div className="hcpc-history-table">
          {!eligibility.history || eligibility.history?.length == 0 ? (
            <div style={{ paddingLeft: 60 }}>No Order History</div>
          ) : (
            <div className={`hcpc-history-table-header${eligibility.productType === "Machine" ? " hcpc-machine" : ""}`}>
              <div>HCPC</div>
              <div>Order Date</div>
              <div>QTY</div>
              <div>Zero Billed</div>
              <div>Private Pay</div>
              <div>Replacement</div>
              {/* <div>Offered</div> */}
              {eligibility.productType === "Machine" && <div>Serial Number</div>}
              <div>Origin</div>
              <div
                style={{
                  display: "flex",
                  flex: 1,
                  justifyContent: "flex-start"
                }}
              >
                Item Name
              </div>
              <div>Item ID</div>
            </div>
          )}
          {eligibility.history?.map((x, i) => (
            <div className="eligibility-history-row-container" key={i}>
              {editRow === x.hcpcOrderedID ? (
                <EditHcpcHistoryRow
                  history={x}
                  eligibility={eligibility}
                  setEditRow={setEditRow}
                  patientId={patientId}
                  updatePatientHCPCHistoryAPI={updatePatientHCPCHistoryAPI}
                />
              ) : (
                <div className={`hcpc-history-table-row${eligibility.productType === "Machine" ? " hcpc-machine" : ""}`}>
                  <div className="hcpc-truncate" title={x.hcpc}>{x.hcpc}</div>
                  <div>{formatDate(x.service_date)}</div>
                  <div>{x.qty}</div>
                  <div
                    style={{
                      display: "flex",
                      flex: 1,
                      width: "100%",
                      alignContent: "center",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <Checkbox
                      value={x.zeroBilled}
                      color={x.zeroBilled ? "#ff7800" : "default"}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flex: 1,
                      width: "100%",
                      alignContent: "center",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <Checkbox
                      value={x.privatePay}
                      color={x.privatePay ? "#ff7800" : "default"}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flex: 1,
                      width: "100%",
                      alignContent: "center",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <Checkbox
                      value={x.replacementItem}
                      color={x.replacementItem ? "#ff7800" : "default"}
                    />
                  </div>
                  {eligibility.productType === "Machine" && (
                    <div className="hcpc-truncate" title={x.serialNumber}>{x.serialNumber}</div>
                  )}
                  <div>
                    {x.creationMethod === "S3" && x.orderId ? (
                      <Link to={`/orders/${x.orderId}`}>
                        {x.creationMethod}
                      </Link>
                    ) : (
                      x.creationMethod
                    )}{" "}
                  </div>
                  <div className="hcpc-truncate" title={x.productName}>
                    {x.productName}
                  </div>
                  <div className="hcpc-truncate" title={x.productID}>{x.productID}</div>
                  <div>
                    {/* Only allow editing if the row was not generated from S3 Order */}
                    {!editRow && !x.replacementItem && (
                      <HasRolePermission
                        allowedRoles={[
                          "Administrator",
                          "CompanyAdministrator",
                          "OfficeAdministrator",
                          "ServiceCoach",
                          "ServiceAdmin"
                        ]}
                      >
                        <IconButton
                          icon={<MdEdit />}
                          onClick={() => {
                            setEditRow(x.hcpcOrderedID);
                          }}
                        />
                      </HasRolePermission>
                    )}
                    {x.creationMethod !== "S3" && (
                      <HasRolePermission
                        allowedRoles={[
                          "Administrator",
                          "CompanyAdministrator",
                          "OfficeAdministrator",
                          "ServiceCoach",
                          "ServiceAdmin"
                        ]}
                      >
                        <IconButton
                          icon={<MdDelete color="red" />}
                          disabled={updatePatientHCPCHistoryAPI.isFetching}
                          onClick={async () => {
                            if (
                              confirm(
                                "Do you really want to remove this record?"
                              )
                            ) {
                              await updatePatientHCPCHistoryAPI.fetchData({
                                hcpcOrderedID: x.hcpcOrderedID,
                                active: false
                              });
                            }
                          }}
                        />
                      </HasRolePermission>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

HcpcRow.propTypes = {
  eligibility: PropTypes.object.isRequired,
  expanded: PropTypes.bool,
  toggleExpanded: PropTypes.func.isRequired,
  editRow: PropTypes.number,
  setEditRow: PropTypes.func.isRequired,
  patientId: PropTypes.string.isRequired
};

export default HcpcRow;

const EditHcpcHistoryRow = ({
  history,
  eligibility,
  setEditRow,
  updatePatientHCPCHistoryAPI
}) => {
  // Validates the qty field on user-initiated changes only. Skips when
  // unchanged from initial — Importer rows may render as qty=0 from partner
  // feeds and we shouldn't block viewing / other-field edits. qty=0 is
  // allowed on save because non-S3 HCPCS history rows don't cascade into
  // an order line item (see PatientsSerializer.UpdatePatientHCPCOrdered).
  // Still rejects negatives and non-numeric input.
  const validateQuantityIfChanged = value => {
    const num = Number(value);
    if (value === history.qty || num === history.qty) return undefined;
    if (value === "" || value === null || value === undefined) return undefined;
    if (Number.isNaN(num) || num < 0) return "Quantity cannot be negative.";
    return undefined;
  };
  return (
    <Formik
      initialValues={{
        hcpcOrderedID: history.hcpcOrderedID,
        quantity: history.qty,
        serialNumber: history.serialNumber,
        zeroBilled: history.zeroBilled,
        privatePay: history.privatePay,
        service_date: formatDate(history.service_date, "yyyy-MM-DD"),
        replacementItem: history.replacementItem
      }}
      onSubmit={async values => {
        await updatePatientHCPCHistoryAPI.fetchData(values);
        setEditRow(null);
      }}
    >
      {({ isSubmitting, submitForm, values }) => (
        <Form>
          <div className={`hcpc-history-table-row${eligibility.productType === "Machine" ? " hcpc-machine" : ""}`}>
            <div className="hcpc-truncate" title={history.hcpc}>{history.hcpc}</div>
            {/* <div>{formatDate(history.service_date)}</div> */}
            <div>
              <FormikField
                name="service_date"
                type="date"
                disabled={history.creationMethod === "S3"}
              />
            </div>
            <div>
              <FormikField
                name="quantity"
                type="number"
                min="0"
                disabled={history.creationMethod === "S3"}
                validate={validateQuantityIfChanged}
              />
            </div>
            <div
              style={{
                display: "flex",
                flex: 1,
                width: "100%",
                alignContent: "center",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <FormikField label="" name="zeroBilled" type="checkbox" disabled={values.privatePay} />
            </div>
            <div
              style={{
                display: "flex",
                flex: 1,
                width: "100%",
                alignContent: "center",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <FormikField label="" name="privatePay" type="checkbox" disabled={values.zeroBilled} />
            </div>
            <div
              style={{
                display: "flex",
                flex: 1,
                width: "100%",
                alignContent: "center",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <FormikField
                label=""
                name="replacementItem"
                type="checkbox"
                disabled
              />
            </div>
            {/* <div */}
            {/*   style={{ */}
            {/*     display: "flex", */}
            {/*     flex: 1, */}
            {/*     width: "100%", */}
            {/*     alignContent: "center", */}
            {/*     alignItems: "center", */}
            {/*     justifyContent: "center" */}
            {/*   }} */}
            {/* > */}
            {/*   <FormikField label="" name="offered" type="checkbox" /> */}
            {/* </div> */}
            {eligibility.productType === "Machine" && (
              <div>
                <FormikField name="serialNumber" type="text" />
              </div>
            )}
            <div>
              {history.creationMethod === "S3" && history.orderId ? (
                <Link to={`/orders/${history.orderId}`}>
                  {history.creationMethod}
                </Link>
              ) : (
                history.creationMethod
              )}{" "}
            </div>
            <div className="hcpc-truncate" title={history.productName}>
              {history.productName}
            </div>
            <div className="hcpc-truncate" title={history.productID}>{history.productID}</div>
            {/* Only allow editing if the row was not generated from S3 Order */}
            {isSubmitting ? (
              <InlineWaiting />
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flex: 1,
                  justifyContent: "space-between"
                }}
              >
                <IconButton
                  type="button"
                  icon={<MdCancel color="orange" size={18} />}
                  onClick={() => {
                    setEditRow(null);
                  }}
                />
                <IconButton
                  type="submit"
                  icon={<MdSave color="green" size={18} />}
                  onClick={submitForm}
                />
              </div>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

EditHcpcHistoryRow.propTypes = {
  history: PropTypes.object.isRequired,
  eligibility: PropTypes.object.isRequired,
  editRow: PropTypes.number,
  setEditRow: PropTypes.func.isRequired,
  updatePatientHCPCHistoryAPI: PropTypes.object.isRequired
};
